import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Download,
  Info,
  TriangleAlert,
  LoaderCircle,
  X,
} from 'lucide-react';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Modal } from '@/components/modal';
import { listEvalResultsForWorkflowRun } from '@/services/evaluation/eval-results-v2';
import { mapEvalResultRecordsToTabItems } from '@/pages/workflow/helper/eval-results-mapper';
import { useGetEvalTypes } from '@/hooks/queries/evaluation/use-get-eval-types';
import { downloadEvalReportCSV } from '@/pages/workflow/helper/eval-report-csv';
import type {
  EvalBadgeVariant,
  EvalResultItem,
  EvalsResultOpensearchLog,
} from '@/types/eval-v2-types';

export type { EvalBadgeVariant, EvalResultItem } from '@/types/eval-v2-types';

function EvalTypeGuideButton({
  evalTypeLabel,
  evalTypeId,
}: {
  evalTypeLabel: string;
  evalTypeId?: string;
}) {
  const [open, setOpen] = useState(false);
  const { data: evalTypes } = useGetEvalTypes();
  const metadata = evalTypeId
    ? evalTypes?.find((t) => t.id === evalTypeId)
    : evalTypes?.find((t) => t.name === evalTypeLabel);
  if (!metadata) return null;
  const description = metadata.description;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Must be a span (not <button>) — this sits inside the
              outer CollapsibleTrigger <button>, and nesting buttons
              is invalid HTML. role/aria/tabIndex make it equivalent,
              and stopPropagation prevents the card from toggling. */}
          <span
            role='button'
            tabIndex={0}
            aria-label='View eval type guide'
            className='shrink-0 cursor-default text-muted-foreground/60 hover:text-muted-foreground'
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
          >
            <Info className='size-3.5' />
          </span>
        </TooltipTrigger>
        <TooltipContent className='whitespace-pre-line'>
          <p>{description}</p>
          <button
            type='button'
            className='mt-1.5 block text-xs underline underline-offset-2 opacity-80 hover:opacity-100'
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
          >
            See more
          </button>
        </TooltipContent>
      </Tooltip>

      <Modal
        title={evalTypeLabel}
        isDialogOpen={open}
        setIsDialogOpen={setOpen}
        contentClassName='max-w-lg'
      >
        <div className='text-sm'>
          {metadata.description &&
            metadata.description.split('\n\n').map((para, i) => (
              <p key={i} className='text-muted-foreground mb-3 last:mb-4'>
                {para}
              </p>
            ))}
          {metadata.sections.map((section) => (
            <div key={section.heading} className='mb-4 last:mb-0'>
              <p className='font-medium mb-1.5'>{section.heading}</p>
              <ul className='space-y-1 text-muted-foreground'>
                {section.items.map((item, i) => (
                  <li key={i} className='flex items-start gap-2'>
                    <span className='mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground' />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {metadata.tip && (
            <div className='mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200'>
              <span className='font-medium'>Tip: </span>
              {metadata.tip}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

function EvalStatusBadge({
  variant,
  text,
}: {
  variant: EvalBadgeVariant;
  text: string;
}) {
  const showCheck = variant === 'pass';
  const showX = variant === 'fail';
  const showAlert = variant === 'error';

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium shadow-sm',
        variant === 'pass' &&
          'border-emerald-500 bg-emerald-100 text-emerald-900',
        variant === 'fail' && 'border-rose-500 bg-rose-100 text-rose-900',
        variant === 'score-high' &&
          'border-emerald-500 bg-emerald-100 text-emerald-900',
        variant === 'score-mid' &&
          'border-yellow-500 bg-yellow-100 text-yellow-900',
        variant === 'error' && 'border-amber-500 bg-amber-100 text-amber-900',
      )}
    >
      {showCheck && <Check className='size-3.5' strokeWidth={2.5} />}
      {showX && <X className='size-3.5' strokeWidth={2.5} />}
      {showAlert && <TriangleAlert className='size-3.5' strokeWidth={2.5} />}
      {text}
    </span>
  );
}

function EvalCardHeader({
  item,
  expandable,
  open,
  hideEvalTypeLabel,
}: {
  item: EvalResultItem;
  expandable: boolean;
  open: boolean;
  hideEvalTypeLabel?: boolean;
}) {
  return (
    <div className='flex w-full min-w-0 items-center gap-2'>
      <div className='flex min-w-0 flex-1 flex-col gap-1 leading-none'>
        <TruncateTextWithTooltip
          text={item.title}
          className='text-sm font-semibold text-foreground'
        />
        {!hideEvalTypeLabel && (
          <div className='flex min-w-0 items-center gap-1'>
            <p className='truncate text-sm text-muted-foreground'>
              {item.evalTypeLabel}
            </p>
            <EvalTypeGuideButton
              evalTypeLabel={item.evalTypeLabel}
              evalTypeId={item.evalTypeId}
            />
          </div>
        )}
      </div>
      <div className='flex shrink-0 flex-col items-end gap-1'>
        <EvalStatusBadge variant={item.badgeVariant} text={item.badgeText} />
        {/* Render the "Previous run" badge only on top-level cards
            (where `hideEvalTypeLabel` is false). The flag is still set on
            children for data consistency, but repeating the badge on
            every per-dimension row inside an already-tagged parent
            card is visually noisy. */}
        {item.fromParentRun && !hideEvalTypeLabel && (
          <span
            title='This result was inherited from a previous run because the eval node was not re-executed in this run.'
            className='rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground'
          >
            Previous run
          </span>
        )}
      </div>
      {expandable ? (
        open ? (
          <ChevronDown className='size-5 shrink-0 text-muted-foreground' />
        ) : (
          <ChevronRight className='size-5 shrink-0 text-muted-foreground' />
        )
      ) : (
        <ChevronRight className='size-5 shrink-0 text-muted-foreground' />
      )}
    </div>
  );
}

function EvalChildCard({ item }: { item: EvalResultItem }) {
  const [open, setOpen] = useState(false);
  const expandable = Boolean(item.detail);

  const hoverOverlay =
    'hover:[background-image:linear-gradient(rgba(0,0,0,0.03),rgba(0,0,0,0.03))] dark:hover:[background-image:linear-gradient(rgba(255,255,255,0.04),rgba(255,255,255,0.04))]';

  if (!expandable) {
    return (
      <div className={cn('rounded-md px-2 py-2.5', hoverOverlay)}>
        <EvalCardHeader
          item={item}
          expandable={false}
          open={false}
          hideEvalTypeLabel
        />
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={cn('rounded-md', hoverOverlay)}>
        <CollapsibleTrigger asChild>
          <button
            type='button'
            className='flex w-full cursor-pointer items-center gap-2 px-2 py-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring'
          >
            <EvalCardHeader
              item={item}
              expandable
              open={open}
              hideEvalTypeLabel
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className='px-2 pb-2 pt-1 space-y-2 text-sm leading-5 text-foreground'>
            {item.detail?.split('\n\n').map((para: string, i: number) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function EvalResultCard({
  item,
  defaultOpen,
}: {
  item: EvalResultItem;
  defaultOpen?: boolean;
}) {
  const hasChildren = Boolean(item.children?.length);
  const expandable = Boolean(item.detail) || hasChildren;
  const [open, setOpen] = useState(!!defaultOpen && expandable);

  const cardHover =
    'hover:[background-image:linear-gradient(rgba(0,0,0,0.03),rgba(0,0,0,0.03))] dark:hover:[background-image:linear-gradient(rgba(255,255,255,0.04),rgba(255,255,255,0.04))]';

  if (!expandable) {
    return (
      <div className={cn('rounded-lg bg-muted p-4', cardHover)}>
        <EvalCardHeader item={item} expandable={false} open={false} />
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={cn('rounded-lg bg-muted p-4', cardHover)}>
        <CollapsibleTrigger asChild>
          <button
            type='button'
            className='flex w-full cursor-pointer items-center gap-2 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-ring'
          >
            <EvalCardHeader item={item} expandable open={open} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {item.detail && (
            <div className='mt-4 space-y-3 text-sm leading-5 text-foreground'>
              {item.detail.split('\n\n').map((para: string, i: number) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          )}
          {hasChildren && (
            <div className='mt-3 flex flex-col border-l-2 border-border pl-3'>
              {item.children!.map((child) => (
                <EvalChildCard key={child.id} item={child} />
              ))}
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export interface EvalNodeInfo {
  nodeId: string;
  name: string;
  /** Number of evals selected in this node's multi-select parameter. */
  evalCount: number;
  /**
   * Display names of nodes connected to this Evals node's input port(s)
   * (the nodes whose output is being scored). Comes from the workflow's
   * edge graph: every node where `edge.target === this.nodeId`.
   * Surfaced in the CSV export so the report stands on its own without
   * having to cross-reference the canvas.
   */
  upstreamNodeNames: string[];
}

export const EvalsTabContent: React.FC<{
  /** Persisted workflow id (omit on unsaved / "new" workflow). */
  workflowId?: string | null;
  /**
   * Workflow run scope. Must be the agentic-backend `session_id` (which the
   * eval node forwards to platform-api as `workflow_run_id`), NOT the
   * platform `WorkflowRun.id`.
   */
  workflowRunId?: string | null;
  /**
   * Source run's session_id when `workflowRunId` is a rerun-from-node run.
   * Used as a per-node fallback for eval nodes that were cached/skipped in
   * the current run (the backend doesn't duplicate OpenSearch eval docs
   * into the new session for cached nodes). Null for fresh runs and pure
   * past-run views.
   */
  parentRunId?: string | null;
  /**
   * react_flow_generated_ids of nodes seeded as cached in the current
   * rerun. Eval nodes in this set fall back to `parentRunId` results;
   * eval nodes NOT in this set are expected to produce fresh results in
   * `workflowRunId` (and the spinner waits for them).
   */
  cachedEvalNodeIds?: Set<string>;
  /**
   * True while the workflow is actively executing. While true, the
   * auto-poll loop runs without a cap (results trickle in as eval nodes
   * finish). Once it flips to false, the cap kicks in: 3 more poll
   * attempts at 5 s spacing, then the spinner gives up — that's the
   * stall detector for a terminated run that's missing results.
   */
  isWorkflowExecuting?: boolean;
  /** Override: supply rows directly instead of fetching (e.g. tests). */
  results?: EvalResultItem[];
  /** All eval nodes present on the canvas (used for the filter dropdown). */
  evalNodes?: EvalNodeInfo[];
  /**
   * When a single eval node (or a node connected to one) is selected on the
   * canvas, pass its `react_flow_generated_id` here so the filter auto-selects.
   */
  autoSelectedEvalNodeId?: string | null;
  /** When set (a node is selected on canvas), the header shows a back button. */
  /** Called from the "All Evals" back button to clear node selection. */
}> = ({
  workflowId,
  workflowRunId,
  parentRunId = null,
  cachedEvalNodeIds,
  isWorkflowExecuting = false,
  results: resultsProp,
  evalNodes = [],
  autoSelectedEvalNodeId,
}) => {
  // 'all' or a specific nodeId
  const [filter, setFilter] = useState<string>('all');
  const [rawRecords, setRawRecords] = useState<
    EvalsResultOpensearchLog[] | null
  >(null);
  // OpenSearch record ids that were sourced from `parentRunId` and merged
  // into `rawRecords` as a fallback for cached eval nodes. Used to tag
  // their UI cards with a "from previous run" badge.
  const [parentRunRecordIds, setParentRunRecordIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Auto-switch filter when a node is selected/deselected on the canvas
  useEffect(() => {
    setFilter(autoSelectedEvalNodeId ?? 'all');
  }, [autoSelectedEvalNodeId]);

  const fetchResults = useCallback(
    async (signal?: AbortSignal) => {
      if (!workflowRunId) return;
      // Fetch the current run + parent run (if any) in parallel.
      // For rerun-from-node, eval nodes that were CACHED don't get fresh
      // OpenSearch eval docs in `workflowRunId` — their docs still live
      // under `parentRunId`. We merge per-`node_id` so each canvas eval
      // node ends up with its most recent applicable result, preferring
      // the current run when both exist (defensive against duplicate
      // writes).
      const [currentRows, parentRows] = await Promise.all([
        listEvalResultsForWorkflowRun(workflowRunId, signal),
        parentRunId
          ? listEvalResultsForWorkflowRun(parentRunId, signal)
          : Promise.resolve<EvalsResultOpensearchLog[]>([]),
      ]);

      const currentNodeIds = new Set(currentRows.map((r) => r.node_id));
      const cachedSet = cachedEvalNodeIds ?? new Set<string>();
      const inheritedRows = parentRows.filter(
        (r) =>
          // Only inherit if the parent's node was cached/skipped in this
          // run AND we don't already have a fresh row for it.
          cachedSet.has(r.node_id) && !currentNodeIds.has(r.node_id),
      );

      setRawRecords([...currentRows, ...inheritedRows]);
      setParentRunRecordIds(new Set(inheritedRows.map((r) => r.id)));
    },
    [workflowRunId, parentRunId, cachedEvalNodeIds],
  );

  useEffect(() => {
    if (resultsProp !== undefined) {
      setRawRecords(null);
      setParentRunRecordIds(new Set());
      setFetchError(null);
      setLoading(false);
      return;
    }
    if (!workflowRunId) {
      setRawRecords(null);
      setParentRunRecordIds(new Set());
      setFetchError(null);
      setLoading(false);
      return;
    }

    const ac = new AbortController();
    setLoading(true);
    setFetchError(null);

    const load = async () => {
      try {
        await fetchResults(ac.signal);
      } catch (e) {
        if (ac.signal.aborted) return;
        setRawRecords([]);
        setParentRunRecordIds(new Set());
        const message =
          e instanceof Error ? e.message : 'Failed to load eval results';
        setFetchError(message);
        Sentry.captureException(e, {
          extra: { context: 'evals-tab-fetch', workflowRunId },
        });
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    };

    void load();
    return () => {
      ac.abort();
    };
  }, [workflowRunId, resultsProp, fetchResults]);

  // Filter the merged record set so both `items` (the cards) and
  // `receivedEvalIds` (the spinner numerator) share one source of truth.
  const scopedRecords = useMemo(() => {
    if (!rawRecords) return null;
    if (filter === 'all') return rawRecords;
    return rawRecords.filter((r) => r.node_id === filter);
  }, [rawRecords, filter]);

  const items: EvalResultItem[] = useMemo(() => {
    if (resultsProp !== undefined) return resultsProp;
    if (!scopedRecords) return [];
    return mapEvalResultRecordsToTabItems(scopedRecords, parentRunRecordIds);
  }, [resultsProp, scopedRecords, parentRunRecordIds]);

  const receivedEvalIds = useMemo(() => {
    if (!scopedRecords) return new Set<string>();
    const ids = new Set<string>();
    for (const rec of scopedRecords) {
      for (const er of rec.eval_results ?? []) {
        if (er.eval_id) ids.add(er.eval_id);
      }
    }
    return ids;
  }, [scopedRecords]);

  // The set of `node_id`s that actually returned results in this view.
  // Used to narrow the expected count for terminated runs that have no
  // way to grow further (e.g. a past run viewed long after it finished,
  // when the canvas has since added/removed eval nodes).
  const respondedNodeIds = useMemo(() => {
    if (!rawRecords?.length) return new Set<string>();
    return new Set(rawRecords.map((r) => r.node_id));
  }, [rawRecords]);

  const expectedEvalCount = useMemo(() => {
    // Single-node filter: exactly that node's eval count.
    if (filter !== 'all') {
      const node = evalNodes.find((n) => n.nodeId === filter);
      return node?.evalCount ?? 0;
    }

    // For an in-progress run, OR a rerun where cached results will be
    // merged from `parentRunId`, every canvas eval node is expected to
    // contribute a result eventually. Use the full canvas count.
    const hasRerunLineage =
      parentRunId != null && (cachedEvalNodeIds?.size ?? 0) > 0;
    if (isWorkflowExecuting || hasRerunLineage) {
      return evalNodes.reduce((sum, n) => sum + n.evalCount, 0);
    }

    // Terminated run with no merge to fall back on (e.g. a pure past
    // run whose canvas has gained eval nodes since it ran). Narrow to
    // nodes that actually returned data so we don't perpetually show
    // "Processing Evals" waiting for results that will never arrive.
    return evalNodes
      .filter((n) => respondedNodeIds.has(n.nodeId))
      .reduce((sum, n) => sum + n.evalCount, 0);
  }, [
    evalNodes,
    filter,
    isWorkflowExecuting,
    parentRunId,
    cachedEvalNodeIds,
    respondedNodeIds,
  ]);

  const resultsIncomplete =
    expectedEvalCount > 0 &&
    rawRecords !== null &&
    !loading &&
    receivedEvalIds.size < expectedEvalCount;

  // Stall-detector cap: after the workflow terminates, poll up to
  // MAX_POST_TERMINATION_POLLS times before declaring the spinner
  // exhausted. While the workflow is still executing, we poll without
  // a cap — results legitimately trickle in over the run's duration.
  const MAX_POST_TERMINATION_POLLS = 3;
  const postTerminationPollsRef = useRef(0);
  const [pollExhausted, setPollExhausted] = useState(false);

  // Reset the cap whenever (a) the run we're viewing changes or (b) the
  // workflow flips from terminated → executing (a new run started).
  // Critically we DON'T reset on the executing → terminated edge — that's
  // the moment we want to start counting toward the cap.
  useEffect(() => {
    postTerminationPollsRef.current = 0;
    setPollExhausted(false);
  }, [workflowRunId, isWorkflowExecuting]);

  const handleRefresh = useCallback(async () => {
    try {
      await fetchResults();
      // Clear any prior error so the tab doesn't stay stuck on the
      // error UI after a successful retry.
      setFetchError(null);
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to load eval results';
      setFetchError(message);
    }
  }, [fetchResults]);

  // Don't auto-poll while in an error state — let the user retry
  // explicitly. Otherwise we'd silently re-attempt every 5 s and the
  // error message would never settle.
  const shouldPoll = resultsIncomplete && !pollExhausted && !fetchError;

  // Auto-poll every 5 s while results are still coming in.
  // - If the workflow is still executing: poll forever (no cap). Eval
  //   results trickle in as nodes finish; we don't know how long that
  //   will take, so we keep polling until the run terminates.
  // - If the workflow has terminated: cap at MAX_POST_TERMINATION_POLLS.
  //   That's the stall detector — a missing result here means the
  //   backend will never deliver it (e.g. an eval activity errored or
  //   OpenSearch write failed), so the spinner shouldn't run forever.
  useEffect(() => {
    if (!shouldPoll) return;
    const timer = setInterval(() => {
      void handleRefresh();
      if (!isWorkflowExecuting) {
        postTerminationPollsRef.current += 1;
        if (postTerminationPollsRef.current >= MAX_POST_TERMINATION_POLLS) {
          setPollExhausted(true);
        }
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [shouldPoll, handleRefresh, isWorkflowExecuting]);

  const handleDownloadReport = useCallback(() => {
    if (!rawRecords?.length) return;
    const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
    downloadEvalReportCSV(rawRecords, evalNodes, `eval-report-${ts}.csv`);
  }, [rawRecords, evalNodes]);

  const downloadReportButton = (
    <Button
      type='button'
      variant='ghost'
      size='sm'
      className='gap-2'
      onClick={handleDownloadReport}
      disabled={!rawRecords?.length}
    >
      Download Report
      <Download className='size-4' />
    </Button>
  );
  const evalCountLabel = `${items.length} ${items.length === 1 ? 'Eval' : 'Evals'}`;

  return (
    <div className='flex flex-1 flex-col min-h-0 overflow-hidden'>
      <div className='flex items-center justify-between pl-5 pr-1.5 py-1.5 shrink-0'>
        <span className='text-sm font-medium text-foreground'>
          {evalCountLabel}
        </span>
        {downloadReportButton}
      </div>
      <Separator />
      <div className='flex-1 overflow-y-auto'>
        {loading ? (
          <div className='flex items-center justify-center h-full text-sm text-muted-foreground'>
            Loading evals…
          </div>
        ) : fetchError ? (
          <div className='flex items-center justify-center h-full text-sm text-destructive'>
            {fetchError}
          </div>
        ) : items.length > 0 ? (
          <div className='flex flex-col gap-1.5 p-4'>
            {resultsIncomplete && !pollExhausted && (
              <div className='flex items-center px-1 py-1.5 text-xs text-muted-foreground'>
                <div className='flex items-center gap-1.5 text-black dark:text-white text-sm'>
                  <LoaderCircle className='size-3.5 animate-spin' />
                  <span className='font-medium'>Processing Evals</span>
                </div>
              </div>
            )}
            {items.map((item, index) => (
              <EvalResultCard
                key={item.id}
                item={item}
                defaultOpen={index === 0}
              />
            ))}
          </div>
        ) : !workflowId ? (
          <div className='flex items-center justify-center h-full text-sm text-muted-foreground'>
            Save this workflow to load eval results.
          </div>
        ) : (
          <div className='flex items-center justify-center h-full text-sm text-muted-foreground'>
            No eval results available
          </div>
        )}
      </div>
    </div>
  );
};
